import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Trash2, Phone, MessageSquare, User, Search, Inbox } from "lucide-react";

export default function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data || []);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((l) => l.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const filtered = leads.filter((l) =>
    [l.name, l.mobile, l.message].join(" ").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="leads-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Enquiries</h2>
          <p className="text-sm text-slate-500">All submissions from the popup form.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              data-testid="leads-search"
              className="rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm focus:border-[#1E3A8A] focus:outline-none"
            />
          </div>
          <button onClick={fetchLeads} data-testid="leads-refresh" className="text-xs font-semibold text-[#1E3A8A] hover:underline">Refresh</button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Total Enquiries" value={leads.length} />
        <Stat label="This Week" value={leads.filter(l => isThisWeek(l.created_at)).length} />
        <Stat label="New Today" value={leads.filter(l => isToday(l.created_at)).length} />
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Inbox className="w-10 h-10 mx-auto mb-3" />
            <div className="text-sm">No enquiries yet.</div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((l) => (
              <li key={l.id} className="p-5 hover:bg-slate-50 transition" data-testid={`lead-row-${l.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] grid place-items-center shrink-0">
                      <User className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F172A]">{l.name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{new Date(l.created_at).toLocaleString()}</div>
                      <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
                        <a href={`tel:${l.mobile}`} className="inline-flex items-center gap-1 text-teal-700 font-medium hover:underline">
                          <Phone className="w-3.5 h-3.5" /> {l.mobile}
                        </a>
                      </div>
                      {l.message && (
                        <div className="mt-2 text-sm text-slate-600 flex items-start gap-2">
                          <MessageSquare className="w-3.5 h-3.5 mt-1 text-slate-400 shrink-0" />
                          <span>{l.message}</span>
                        </div>
                      )}
                      {(l.course || l.source) && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {l.course && (
                            <span className="pill" data-testid={`lead-course-${l.id}`}>{l.course}</span>
                          )}
                          {l.source && l.source !== "website" && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                              via {l.source}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => del(l.id)}
                    data-testid={`lead-delete-${l.id}`}
                    className="text-slate-400 hover:text-red-600 transition p-2 rounded"
                    aria-label="delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-3xl font-extrabold text-[#1E3A8A] mt-1">{value}</div>
    </div>
  );
}

function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.toDateString() === n.toDateString();
}
function isThisWeek(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return (n - d) / (1000 * 60 * 60 * 24) <= 7;
}
